import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { payoutPeriodLabel } from "@/lib/payout-period";
import { api, type PayoutRequest, type PayoutRequestStatus } from "@/api";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@osmix/ui/card";
import { Input } from "@osmix/ui/input";
import { orderNo } from "@/lib/short-numbers";
import { formatPrice } from "@/lib/utils";
import { hasAdminPermission } from "@/lib/admin-permissions";
import { useAuth } from "@/store";
const STATUSES: (PayoutRequestStatus | "all")[] = ["new", "approved", "paid", "rejected", "all"];
const STATUS_LABEL: Record<PayoutRequestStatus | "all", string> = {
  new: "Новые",
  approved: "Одобрены",
  paid: "Выплачены",
  rejected: "Отклонены",
  all: "Все"
};

/** Из какого статуса куда можно перейти — зеркало правила на шлюзе. */
const NEXT: Record<PayoutRequestStatus, PayoutRequestStatus[]> = {
  new: ["approved", "rejected"],
  approved: ["paid", "rejected"],
  rejected: [],
  paid: []
};
const ACTION_LABEL: Record<string, string> = {
  approved: "Одобрить",
  paid: "Отметить выплаченной",
  rejected: "Отклонить"
};

/**
 * Четыре числа заявки в один ряд (встреча 25.09.2026): сколько запрошено,
 * сколько было доступно к выводу и начислено за месяц на момент подачи, и
 * сколько продавец НЕ запросил. Остаток — кредиторская задолженность площадки
 * перед продавцом, её надо видеть, а не вычислять в уме.
 */
function PayoutFigures(t0) {
  const $ = _c(5);
  const {
    request
  } = t0;
  let t1;
  if ($[0] !== request.accruedAtRequest || $[1] !== request.amount || $[2] !== request.availableAtRequest || $[3] !== request.notRequested) {
    const cells = [["\u0417\u0430\u043F\u0440\u043E\u0448\u0435\u043D\u043E", request.amount, "font-semibold"], ["\u0414\u043E\u0441\u0442\u0443\u043F\u043D\u043E \u043D\u0430 \u043C\u043E\u043C\u0435\u043D\u0442 \u0437\u0430\u044F\u0432\u043A\u0438", request.availableAtRequest], ["\u041D\u0430\u0447\u0438\u0441\u043B\u0435\u043D\u043E \u0437\u0430 \u043C\u0435\u0441\u044F\u0446", request.accruedAtRequest], ["\u041D\u0435 \u0437\u0430\u043F\u0440\u043E\u0448\u0435\u043D\u043E (\u043E\u0441\u0442\u0430\u0442\u043E\u043A)", request.notRequested, request.notRequested ? "text-amber-700 dark:text-amber-400" : undefined]];
    t1 = <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">{cells.map(_temp)}</div>;
    $[0] = request.accruedAtRequest;
    $[1] = request.amount;
    $[2] = request.availableAtRequest;
    $[3] = request.notRequested;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}

/**
 * Из чего сложилась сумма заявки. Без этого «Одобрить» — решение вслепую:
 * менеджер видит только итог и не может проверить, за какие продажи платит.
 *
 * Цифры шлюз считает теми же запросами, что и отчёт в кабинете продавца, а
 * список заказов — ровно те заказы, что вошли в продажи месяца.
 */
function _temp(t0) {
  const [label, value, cls] = t0;
  return <div key={label} className="rounded-md border px-3 py-2"><p className="text-[11px] leading-tight text-muted-foreground">{label}</p><p className={`mt-0.5 text-sm tabular-nums ${cls ?? ""}`}>{value === undefined ? "\u2014" : formatPrice(value)}</p></div>;
}
function PayoutBreakdown(t0) {
  const $ = _c(58);
  const {
    request
  } = t0;
  const {
    t
  } = useTranslation();
  const [ordersOpen, setOrdersOpen] = useState(false);
  let t1;
  if ($[0] !== request.id) {
    t1 = {
      queryKey: ["payouts", "details", request.id],
      queryFn: () => api.finances.payoutDetails(request.id)
    };
    $[0] = request.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const details = useQuery(t1);
  if (details.isPending) {
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <p className="text-xs text-muted-foreground">Считаю, из чего сложилась сумма…</p>;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  if (details.isError) {
    let t2;
    if ($[3] !== details.error.message) {
      t2 = <p className="text-xs text-destructive">Не удалось загрузить расшифровку: {details.error.message}</p>;
      $[3] = details.error.message;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  const {
    statement,
    orders,
    sellerUserId
  } = details.data;
  const exceeds = request.status !== "paid" && request.amount > statement.accrued + 0.009;
  let t2;
  if ($[5] !== statement.sales) {
    t2 = ["\u041F\u0440\u043E\u0434\u0430\u0436\u0438 \u0437\u0430 \u043C\u0435\u0441\u044F\u0446", statement.sales];
    $[5] = statement.sales;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = -statement.commission;
  let t4;
  if ($[7] !== t3) {
    t4 = ["\u041A\u043E\u043C\u0438\u0441\u0441\u0438\u044F \u043F\u043B\u043E\u0449\u0430\u0434\u043A\u0438", t3];
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = -statement.refunds;
  let t6;
  if ($[9] !== t5) {
    t6 = ["\u0412\u043E\u0437\u0432\u0440\u0430\u0442\u044B", t5];
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== statement.sellerDelivery) {
    t7 = statement.sellerDelivery ? [["\u0414\u043E\u0441\u0442\u0430\u0432\u043A\u0430 \u0441\u0438\u043B\u0430\u043C\u0438 \u043F\u0440\u043E\u0434\u0430\u0432\u0446\u0430", statement.sellerDelivery]] as [string, number][] : [];
    $[11] = statement.sellerDelivery;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== statement.accrued) {
    t8 = ["\u041D\u0430\u0447\u0438\u0441\u043B\u0435\u043D\u043E \u043F\u0440\u043E\u0434\u0430\u0432\u0446\u0443", statement.accrued, "font-medium"];
    $[13] = statement.accrued;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let rows;
  if ($[15] !== statement.available || $[16] !== statement.paid || $[17] !== statement.pending || $[18] !== t2 || $[19] !== t4 || $[20] !== t6 || $[21] !== t7 || $[22] !== t8) {
    rows = [t2, t4, t6, ...t7, t8];
    if (statement.paid > 0) {
      const t9 = -statement.paid;
      let t10;
      if ($[24] !== t9) {
        t10 = ["\u0423\u0436\u0435 \u0432\u044B\u043F\u043B\u0430\u0447\u0435\u043D\u043E \u0437\u0430 \u043C\u0435\u0441\u044F\u0446", t9];
        $[24] = t9;
        $[25] = t10;
      } else {
        t10 = $[25];
      }
      rows.push(t10);
    }
    if (statement.pending > 0) {
      const t9 = -statement.pending;
      let t10;
      if ($[26] !== t9) {
        t10 = ["\u0412 \u0437\u0430\u044F\u0432\u043A\u0430\u0445 (\u0432\u043A\u043B\u044E\u0447\u0430\u044F \u044D\u0442\u0443)", t9];
        $[26] = t9;
        $[27] = t10;
      } else {
        t10 = $[27];
      }
      rows.push(t10);
    }
    let t9;
    if ($[28] !== statement.available) {
      t9 = ["\u0414\u043E\u0441\u0442\u0443\u043F\u043D\u043E \u043A \u0432\u044B\u0432\u043E\u0434\u0443 \u0441\u0435\u0439\u0447\u0430\u0441", statement.available, "font-medium"];
      $[28] = statement.available;
      $[29] = t9;
    } else {
      t9 = $[29];
    }
    rows.push(t9);
    $[15] = statement.available;
    $[16] = statement.paid;
    $[17] = statement.pending;
    $[18] = t2;
    $[19] = t4;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = rows;
  } else {
    rows = $[23];
  }
  let t9;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Из чего сумма</p>;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== sellerUserId) {
    t10 = sellerUserId && <Link to={`/shops/seller/${sellerUserId}`} className="inline-flex items-center gap-1 text-xs text-primary hover:underline">Карточка продавца<ExternalLink className="size-3" /></Link>;
    $[31] = sellerUserId;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== t10) {
    t11 = <div className="flex flex-wrap items-center justify-between gap-2">{t9}{t10}</div>;
    $[33] = t10;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== rows) {
    t12 = <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1">{rows.map(_temp2)}</dl>;
    $[35] = rows;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== exceeds || $[38] !== request.amount || $[39] !== statement.accrued) {
    t13 = exceeds && <p className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">Заявка больше начисленного за месяц на {formatPrice(request.amount - statement.accrued)}: после подачи были возвраты или отмены. Сверьте заказы до одобрения.</p>;
    $[37] = exceeds;
    $[38] = request.amount;
    $[39] = statement.accrued;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => setOrdersOpen(_temp3);
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  const t15 = `size-3 transition-transform ${ordersOpen ? "rotate-180" : ""}`;
  let t16;
  if ($[42] !== t15) {
    t16 = <ChevronDown className={t15} />;
    $[42] = t15;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  const t17 = orders.length === 0 ? "\u0417\u0430\u043A\u0430\u0437\u043E\u0432 \u0437\u0430 \u043C\u0435\u0441\u044F\u0446 \u043D\u0435\u0442" : `Заказы за месяц: ${orders.length}`;
  let t18;
  if ($[44] !== ordersOpen || $[45] !== t16 || $[46] !== t17) {
    t18 = <button type="button" className="inline-flex items-center gap-1 self-start text-xs text-primary hover:underline" onClick={t14} aria-expanded={ordersOpen}>{t16}{t17}</button>;
    $[44] = ordersOpen;
    $[45] = t16;
    $[46] = t17;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== orders || $[49] !== ordersOpen || $[50] !== t) {
    t19 = ordersOpen && orders.length > 0 && <div className="overflow-x-auto"><table className="w-full text-xs"><thead className="text-muted-foreground"><tr className="text-left"><th className="py-1 pr-3 font-normal">Заказ</th><th className="py-1 pr-3 font-normal">Дата</th><th className="py-1 pr-3 font-normal">Статус</th><th className="py-1 pr-3 text-right font-normal">Товары</th><th className="py-1 text-right font-normal">Возврат</th></tr></thead><tbody>{orders.map(order => <tr key={order.id} className="border-t"><td className="py-1 pr-3 tabular-nums"><Link to={`/orders?order=${encodeURIComponent(order.id)}`} className="text-primary hover:underline" title={order.id}>{orderNo(order.id, order.number)}</Link></td><td className="py-1 pr-3">{new Date(order.createdAt).toLocaleDateString("ru-RU")}</td><td className="py-1 pr-3">{t(`orders.status.${order.status}`, {
                defaultValue: order.status
              })}</td><td className="py-1 pr-3 text-right tabular-nums">{formatPrice(order.goods)}</td><td className="py-1 text-right tabular-nums">{order.refunded > 0 ? `− ${formatPrice(order.refunded)}` : "\u2014"}</td></tr>)}</tbody></table></div>;
    $[48] = orders;
    $[49] = ordersOpen;
    $[50] = t;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== t11 || $[53] !== t12 || $[54] !== t13 || $[55] !== t18 || $[56] !== t19) {
    t20 = <div className="flex flex-col gap-2 rounded-md bg-muted/40 p-3 text-sm">{t11}{t12}{t13}{t18}{t19}</div>;
    $[52] = t11;
    $[53] = t12;
    $[54] = t13;
    $[55] = t18;
    $[56] = t19;
    $[57] = t20;
  } else {
    t20 = $[57];
  }
  return t20;
}
function _temp3(v) {
  return !v;
}
function _temp2(t0) {
  const [label, value, cls] = t0;
  return <div key={label} className={`contents ${cls ?? ""}`}><dt className="text-muted-foreground">{label}</dt><dd className="text-right tabular-nums">{value < 0 ? `− ${formatPrice(-value)}` : formatPrice(Math.abs(value))}</dd></div>;
}
const PAGE_SIZE = 20;

/**
 * Воронка выплат продавцам.
 *
 * Продавец запрашивает деньги за закрытый месяц, площадка проводит заявку
 * «Новая → Одобрена → Выплачена». Переходы односторонние: проведённую заявку
 * трогать нельзя, иначе «Выплачена» перестаёт означать факт перевода денег.
 * Отказ требует причины — она уходит продавцу в кабинет.
 */
export function PayoutBoard() {
  const {
    t
  } = useTranslation();
  const user = useAuth(st => st.user);
  // Проводить выплаты может менеджер расчётов и бухгалтер; остальным с
  // доступом к «Финансам» кнопки только мешают — шлюз ответит 403.
  const canDecide = hasAdminPermission(user, "action.finances.manage") || hasAdminPermission(user, "action.payouts.manage");
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<PayoutRequestStatus | "all">("new");
  const [comment, setComment] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // «Выплачена» — факт перевода денег, шаг назад невозможен: подтверждаем.
  const [confirmPaid, setConfirmPaid] = useState<PayoutRequest | null>(null);

  // Страницами с сервера: «Выплачена» копится каждый месяц у каждого магазина.
  const [page, setPage] = useState(1);
  const pageQuery = useQuery({
    queryKey: ["payouts", status, page],
    queryFn: () => api.finances.payoutPage(status, page, PAGE_SIZE),
    placeholderData: keepPreviousData
  });
  const list = {
    isPending: pageQuery.isPending,
    data: pageQuery.data?.items
  };
  const total = pageQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const decide = async (request: PayoutRequest, next: PayoutRequestStatus) => {
    setBusy(request.id);
    setError(null);
    try {
      await api.finances.decidePayout(request.id, {
        status: next as Exclude<PayoutRequestStatus, "new">,
        comment: comment[request.id]?.trim() || undefined
      });
      await queryClient.invalidateQueries({
        queryKey: ["payouts"]
      });
      setComment(prev => ({
        ...prev,
        [request.id]: ""
      }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };
  const month = payoutPeriodLabel;
  return <Card data-tour="finances-payouts">
			<CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
				<CardTitle>{t("payouts.title", {
          defaultValue: "Выплаты продавцам"
        })}</CardTitle>
				<div className="flex flex-wrap gap-1">
					{STATUSES.map(value => <Button key={value} size="sm" variant={value === status ? "default" : "outline"} onClick={() => {
          setStatus(value);
          setPage(1);
        }}>
							{STATUS_LABEL[value]}
						</Button>)}
				</div>
			</CardHeader>
			<CardContent className="flex flex-col gap-3">
				{error && <p className="text-sm text-destructive">{error}</p>}
				{list.isPending ? <p className="text-sm text-muted-foreground">
						{t("common.loading", {
          defaultValue: "Загрузка…"
        })}
					</p> : (list.data?.length ?? 0) === 0 ? <p className="text-sm text-muted-foreground">
						{t("payouts.empty", {
          defaultValue: "Заявок в этом статусе нет."
        })}
					</p> : list.data?.map(request_0 => <div key={request_0.id} className="flex flex-col gap-2 rounded-lg border p-3">
							<div className="flex flex-wrap items-center justify-between gap-2">
								<div className="min-w-0">
									<p className="font-medium">{request_0.shopName ?? request_0.shopId}</p>
									<p className="text-xs text-muted-foreground">
										{month(request_0.period)} · заявка от{" "}
										{new Date(request_0.createdAt).toLocaleDateString("ru-RU")}
										{request_0.account ? ` · ${request_0.account.label}` : ""}
									</p>
								</div>
								<div className="flex items-center gap-2">
									<span className="text-base font-semibold tabular-nums">
										{formatPrice(request_0.amount)}
									</span>
									<Badge variant={request_0.status === "rejected" ? "destructive" : "secondary"}>
										{STATUS_LABEL[request_0.status]}
									</Badge>
								</div>
							</div>

							{/* Кому уходят деньги. Отказ реестра — стоп для одобрения, и
           менеджер должен видеть его до нажатия кнопки, а не в ошибке. */}
							{request_0.counterparty && !request_0.counterparty.active && <p className={request_0.counterparty.unknown ? "rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-800 dark:text-amber-300" : "rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive"}>
									{request_0.counterparty.unknown ? "Реестр не подтвердил контрагента: ИНН не проверен или проверка недоступна. Решение за вами." : `Контрагент в реестре не действующий (${request_0.counterparty.registryStatus}) — выплату проводить нельзя.`}
								</p>}

							<PayoutFigures request={request_0} />

							<PayoutBreakdown request={request_0} />

							{request_0.sellerComment && <p className="text-sm text-muted-foreground">
									Продавец: {request_0.sellerComment}
								</p>}
							{request_0.adminComment && <p className="text-sm">Площадка: {request_0.adminComment}</p>}

							{request_0.paymentOrderNumber && <p className="text-xs text-muted-foreground">
									Платёжное поручение № {request_0.paymentOrderNumber}
									{request_0.exportedAt ? ` · выгружено в 1С ${new Date(request_0.exportedAt).toLocaleDateString("ru-RU")}` : ""}
								</p>}

							{canDecide && NEXT[request_0.status].length > 0 && <div className="flex flex-wrap items-center gap-2">
									<Input className="h-8 min-w-[220px] flex-1" placeholder={t("payouts.comment", {
            defaultValue: "Комментарий (обязателен при отказе)"
          })} value={comment[request_0.id] ?? ""} onChange={e_0 => setComment(prev_0 => ({
            ...prev_0,
            [request_0.id]: e_0.target.value
          }))} />
									{NEXT[request_0.status].map(next_0 => {
            // Реестр сказал «не действующий» — проводить нельзя, но
            // отклонить можно всегда, иначе заявка зависнет навсегда.
            const blocked = next_0 !== "rejected" && request_0.counterparty != null && !request_0.counterparty.active && !request_0.counterparty.unknown;
            return <Button key={next_0} size="sm" variant={next_0 === "rejected" ? "outline" : "default"} disabled={busy === request_0.id || blocked} onClick={() => next_0 === "paid" ? setConfirmPaid(request_0) : void decide(request_0, next_0)}>
												{ACTION_LABEL[next_0]}
											</Button>;
          })}
								</div>}
						</div>)}
				{total > PAGE_SIZE && <div className="flex items-center justify-end gap-2 text-sm text-muted-foreground" data-testid="payouts-pager">
						<span className="tabular-nums">
							{t("pagination.range", {
            from: (page - 1) * PAGE_SIZE + 1,
            to: Math.min(page * PAGE_SIZE, total),
            total
          })}
						</span>
						<Button variant="outline" size="icon" className="size-8" disabled={page <= 1} onClick={() => setPage(page - 1)} aria-label={t("pagination.prev")}>
							<ChevronLeft className="size-4" />
						</Button>
						<Button variant="outline" size="icon" className="size-8" disabled={page >= pageCount} onClick={() => setPage(page + 1)} aria-label={t("pagination.next")}>
							<ChevronRight className="size-4" />
						</Button>
					</div>}
			</CardContent>
			<ConfirmDialog open={confirmPaid !== null} title={`Отметить выплату ${confirmPaid ? formatPrice(confirmPaid.amount) : ""} выполненной?`} description={confirmPaid ? `${confirmPaid.shopName ?? confirmPaid.shopId}, ${month(confirmPaid.period)}. Отметьте после того, как платёжное поручение исполнено банком: продавец увидит «Выплачено» и получит уведомление, отменить отметку нельзя.` : undefined} confirmLabel="Выплачено" busy={busy !== null} onClose={() => setConfirmPaid(null)} onConfirm={() => {
      const request_1 = confirmPaid;
      if (!request_1) return;
      void decide(request_1, "paid").finally(() => setConfirmPaid(null));
    }} />
		</Card>;
}